/**
 * Development utility: Hoodie API & session diagnostics.
 * Read-only checks for hoodie backend config, CSRF cookie, and endpoint reachability.
 * Do not use for production user flows.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  HOODIE_API_BASE_URL,
  isHoodieApiConfigured,
  getEvents,
  getMyProfile,
} from "../../../api";
import "../styles/TwentyTwentysixContent.css";

const CSRF_COOKIE_NAME = "csrf_token";

function getCsrfPresent() {
  if (typeof document === "undefined" || !document.cookie) return false;
  return new RegExp("(?:^|;\\s*)" + CSRF_COOKIE_NAME + "=").test(document.cookie);
}

export default function HoodieDiagnosticsPage() {
  const [unauthenticatedStatus, setUnauthenticatedStatus] = useState(null);
  const [authenticatedStatus, setAuthenticatedStatus] = useState(null);

  const configured = isHoodieApiConfigured();
  const csrfPresent = getCsrfPresent();

  useEffect(() => {
    if (!configured) return;

    getEvents()
      .then((data) => {
        setUnauthenticatedStatus({
          ok: true,
          message: "Unauthenticated endpoint reachable",
          detail: Array.isArray(data) ? `${data.length} events` : "data received",
        });
      })
      .catch((err) => {
        setUnauthenticatedStatus({
          ok: false,
          message: "Unauthenticated endpoint request failed",
          detail: err.message || String(err.status || err),
        });
      });

    getMyProfile()
      .then(() => {
        setAuthenticatedStatus({
          ok: true,
          message: "Authenticated hoodie session detected",
          detail: "hoodies/me/ returned 200",
        });
      })
      .catch((err) => {
        const status = err.status || err.response?.status;
        setAuthenticatedStatus({
          ok: false,
          message: status === 401 || status === 403
            ? "Authenticated hoodie session not detected"
            : "Authenticated endpoint request failed",
          detail: err.message || `HTTP ${status || "error"}`,
        });
      });
  }, [configured]);

  return (
    <div className="twentytwentysix-content">
      <div className="twentytwentysix-content__container">
        <p className="twentytwentysix-content__eyebrow">Development utility</p>
        <h1 className="twentytwentysix-content__title">Hoodie API diagnostics</h1>
        <p className="twentytwentysix-content__copy">
          Read-only checks for hoodie backend configuration and session. Not for production user flows.
        </p>
        <div className="twentytwentysix-content__cta-row">
          <Link to="/twentytwentysix" className="twentytwentysix-content__pill">Back to Home</Link>
        </div>

        <section className="twentytwentysix-content__section" style={{ fontFamily: "monospace", fontSize: "0.9rem" }}>
          <h2 className="twentytwentysix-content__subtitle">Configuration</h2>
          <ul className="twentytwentysix-content__list" style={{ listStyle: "none", padding: 0 }}>
            <li style={{ marginBottom: "8px" }}>
              <strong>Hoodie backend configured:</strong>{" "}
              {configured ? "Yes" : "No (REACT_APP_HOODIE_API_BASE_URL not set)"}
            </li>
            {configured && (
              <li style={{ marginBottom: "8px" }}>
                <strong>Hoodie API base URL:</strong> {HOODIE_API_BASE_URL}
              </li>
            )}
            <li style={{ marginBottom: "8px" }}>
              <strong>CSRF cookie ({CSRF_COOKIE_NAME}):</strong>{" "}
              {csrfPresent ? "Present" : "Not present"}
            </li>
          </ul>
        </section>

        <section className="twentytwentysix-content__section" style={{ fontFamily: "monospace", fontSize: "0.9rem" }}>
          <h2 className="twentytwentysix-content__subtitle">Endpoint checks</h2>
          <ul className="twentytwentysix-content__list" style={{ listStyle: "none", padding: 0 }}>
            <li style={{ marginBottom: "12px", padding: "8px", background: "#f5f5f5", borderRadius: "6px" }}>
              <strong>Unauthenticated (e.g. events/):</strong>{" "}
              {unauthenticatedStatus
                ? `${unauthenticatedStatus.ok ? "✓" : "✗"} ${unauthenticatedStatus.message} — ${unauthenticatedStatus.detail}`
                : "Checking…"}
            </li>
            <li style={{ marginBottom: "12px", padding: "8px", background: "#f5f5f5", borderRadius: "6px" }}>
              <strong>Authenticated (hoodies/me/):</strong>{" "}
              {authenticatedStatus
                ? `${authenticatedStatus.ok ? "✓" : "—"} ${authenticatedStatus.message} — ${authenticatedStatus.detail}`
                : "Checking…"}
            </li>
          </ul>
        </section>

        <p className="twentytwentysix-content__copy" style={{ marginTop: "16px", color: "var(--ac-muted)" }}>
          To get an authenticated session with the hoodie backend, log in via the Django backend (same origin or proxy). This app does not perform Django login itself.
        </p>
      </div>
    </div>
  );
}
